import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmUserAttribute, sendUserAttributeVerificationCode } from 'aws-amplify/auth';
import { UsersApi } from 'services/users/users.api';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser, getSetAuthUser } from 'store/useUserStore/selectors';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IStepFormProps as Props } from 'interfaces/auth';
import { IEnterCodeStepValues } from 'interfaces/confirmationCode';

import { useChangeEmailContext } from 'components/ChangeEmail/ChangeEmailContextProvider';
import { ConfirmationCodeForm } from 'components/ConfirmationCodeForm/ConfirmationCodeForm';

import ToastService from 'helpers/ToastService';

export const EnterConfirmationCodeStep: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { newEmail, prevStep, setNewEmail } = useChangeEmailContext();
  const userDetails = useUserStore(getAuthUser);
  const setUserDetails = useUserStore(getSetAuthUser);

  const { track, isPending } = usePromiseStatus();

  const handleModalClose = () => {
    setNewEmail('');
    onClose();
  }

  const confirmEmailReset = async ({ code }: IEnterCodeStepValues): Promise<void> => {
    try {
      await track(
        confirmUserAttribute({
          userAttributeKey: 'email',
          confirmationCode: code,
        }),
      );

      const updateUserMail = await track(UsersApi.updateUserEmail({ oldEmail: userDetails?.email ?? '', newEmail }));

      setUserDetails({
        firstName: userDetails?.firstName ?? '',
        lastName: userDetails?.lastName ?? '',
        loginType: userDetails?.loginType ?? null,
        ...updateUserMail,
      });

      handleModalClose();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  const resendConfirmationCode = async (): Promise<void> => {
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'email',
      });
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  return (
    <ConfirmationCodeForm
      emailValue={newEmail}
      isPending={isPending}
      formText={t('codeConfirmationMessageShort', { email: newEmail })}
      backClickHandler={prevStep}
      closeClickHandler={handleModalClose}
      resendCodeHandler={resendConfirmationCode}
      onSubmit={confirmEmailReset}
    />
  );
};
